import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auditoria } from "../../../types/auditoria";
import { CronogramaAuditoria } from "./CronogramaAuditoria";
import { ChevronDown, ChevronUp } from "lucide-react";

interface AuditoriaCardProps {
  auditoria: Auditoria;
  onViewComplete: (id: number) => void;
  onDelete?: (id: number) => void;
  onRiskMatrix?: (id: number) => void;
}

export const AuditoriaCard: React.FC<AuditoriaCardProps> = ({
  auditoria,
  onViewComplete,
  onDelete,
  onRiskMatrix,
}) => {
  const navigate = useNavigate();
  const [showCronograma, setShowCronograma] = useState(false);

  // ¿tiene subtareas con fechas?
  const hasTasks = auditoria.categorias?.some((cat) =>
    cat.subtareas?.some((s) => s.fecha_solicitud || s.tiempo_entrega),
  );
  // Formatear fecha
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "Planeación";

    try {
      // Manejar formato ISO con zona horaria
      let date: Date;
      if (dateString.includes("T")) {
        // Formato ISO: 2025-11-17T00:00:00.000000Z
        date = new Date(dateString);
      } else {
        // Formato simple: 2025-11-17
        date = new Date(dateString + "T00:00:00");
      }

      if (isNaN(date.getTime())) return "Planeación";

      const day = date.getUTCDate();
      const monthNames = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];
      const month = monthNames[date.getUTCMonth()];

      return `${day} ${month}`;
    } catch {
      return "Planeación";
    }
  };

  // Calcular porcentajes de progreso basados en subtareas
  const calculateProgress = () => {
    if (!auditoria.categorias || auditoria.categorias.length === 0) {
      return {
        na: 0,
        check: 0,
        pendiente: 0,
        enProceso: 0,
        total: 0,
      };
    }

    // Obtener todas las subtareas
    const allSubtareas = auditoria.categorias.flatMap(
      (cat) => cat.subtareas || [],
    );
    const totalSubtareas = allSubtareas.length;

    if (totalSubtareas === 0) {
      return {
        na: 0,
        check: 0,
        pendiente: 0,
        enProceso: 0,
        total: 0,
      };
    }

    // Contar subtareas por estado
    const sinArchivo = allSubtareas.filter((s) => !s.archivo_nombre).length;
    const aprobadas = allSubtareas.filter(
      (s) => s.estado_informacion === "aprobado",
    ).length;
    const pendientes = allSubtareas.filter(
      (s) =>
        s.estado_informacion === "pendiente" ||
        (!s.estado_informacion && s.archivo_nombre),
    ).length;
    const enProceso = allSubtareas.filter(
      (s) =>
        s.estado_informacion === "recibido" ||
        s.estado_informacion === "revision",
    ).length;
    const conArchivo = allSubtareas.filter((s) => s.archivo_nombre).length;

    return {
      na: (sinArchivo / totalSubtareas) * 100,
      check: (aprobadas / totalSubtareas) * 100,
      pendiente: (pendientes / totalSubtareas) * 100,
      enProceso: (enProceso / totalSubtareas) * 100,
      total: (conArchivo / totalSubtareas) * 100,
    };
  };

  const progress = calculateProgress();

  const checkPct = progress.check;
  const pendientePct = 100 - progress.check;

  const getEstadoBadge = (estado: Auditoria["estado"]) => {
    const badges: Record<string, string> = {
      pendiente: "text-red-500",
      en_progreso: "text-yellow-600",
      completada: "text-green-600",
      aprobado: "text-blue-600",
      check: "text-green-600",
    };
    const labels: Record<string, string> = {
      pendiente: "Pendiente",
      en_progreso: "En Progreso",
      completada: "Completada",
      aprobado: "Aprobado",
      check: "Check",
    };
    return (
      <span className={`font-medium ${badges[estado] ?? "text-gray-600"}`}>
        {labels[estado] ?? estado}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
      {/* Mobile Layout */}
      <div className="lg:hidden">
        <div className="flex items-start gap-3 mb-3">
          {/* Radio button */}
          <div className="flex-1 space-y-2">
            {/* NIT y Razón Social */}
            <div>
              <div className="text-xs text-gray-500">NIT</div>
              <div className="text-sm font-medium text-gray-900">
                {auditoria.empresa?.nit || auditoria.nit || "-"}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Razón social</div>
              <div className="text-sm font-medium text-gray-900">
                {auditoria.empresa?.razon_social ||
                  auditoria.razon_social ||
                  "-"}
                {auditoria.tipo_auditoria && ` - ${auditoria.tipo_auditoria}`}
              </div>
              {auditoria.npr !== undefined && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                  NPR {Number(auditoria.npr).toFixed(0)} ·{" "}
                  {auditoria.nivel_riesgo || "Nivel"}
                </div>
              )}
            </div>

            {/* Fecha y Estado */}
            <div className="flex gap-4">
              <div>
                <div className="text-xs text-gray-500">Fecha inicial</div>
                <div className="text-sm font-medium text-gray-900">
                  {formatDate(auditoria.fecha_inicial)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Fecha corte</div>
                <div className="text-sm font-medium text-gray-900">
                  {formatDate(auditoria.fecha_corte)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Estado</div>
                <div className="text-sm">
                  {getEstadoBadge(auditoria.estado)}
                </div>
              </div>
            </div>

            {/* Proceso - Compact version */}
            <div>
              <div className="text-xs text-gray-500 mb-2">Proceso</div>
              {/* Progress bar */}
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${checkPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs text-gray-500">Completado</span>
                  <span className="text-xs font-semibold text-gray-800">
                    {checkPct.toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="text-xs text-gray-500">Pendiente</span>
                  <span className="text-xs font-semibold text-gray-800">
                    {pendientePct.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          {onDelete && (
            <button
              onClick={() => onDelete(auditoria.id)}
              className="border border-red-300 text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              title="Mover a papelera"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
          {onRiskMatrix && (
            <button
              onClick={() => onRiskMatrix(auditoria.id)}
              className="border border-orange-300 text-orange-600 hover:bg-orange-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              title="Abrir matriz de riesgo"
            >
              Matriz de riesgo
            </button>
          )}
          <button
            onClick={() =>
              navigate(`/auditorias/${auditoria.id}/informe-preliminar`)
            }
            className="flex-1 bg-gray-50 border-2 border-gray-400 text-gray-700 hover:bg-gray-100 hover:border-gray-500 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Crear Informe
          </button>
          <button
            onClick={() => onViewComplete(auditoria.id)}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            Ver auditoría
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-12 gap-4 items-center">
          {/* Radio button */}

          {/* NIT */}
          <div className="col-span-2">
            <div className="text-xs text-gray-500 mb-1">NIT</div>
            <div className="text-sm font-medium text-gray-900">
              {auditoria.empresa?.nit || auditoria.nit || "-"}
            </div>
          </div>

          {/* Razón Social */}
          <div className="col-span-2">
            <div className="text-xs text-gray-500 mb-1">Razón social</div>
            <div className="text-sm font-medium text-gray-900">
              {auditoria.empresa?.razon_social || auditoria.razon_social || "-"}
              {auditoria.tipo_auditoria && ` - ${auditoria.tipo_auditoria}`}
            </div>
          </div>

          {/* Fecha inicial */}
          <div className="col-span-1">
            <div className="text-xs text-gray-500 mb-1">Fecha inicial</div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(auditoria.fecha_inicial)}
            </div>
          </div>

          {/* Fecha corte */}
          <div className="col-span-1">
            <div className="text-xs text-gray-500 mb-1">Fecha corte</div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(auditoria.fecha_corte)}
            </div>
          </div>

          {/* Estado */}
          <div className="col-span-2">
            <div className="text-xs text-gray-500 mb-1">Estado</div>
            <div className="text-sm">{getEstadoBadge(auditoria.estado)}</div>
          </div>

          {/* Proceso - Progress indicators */}
          <div className="col-span-4">
            <div className="text-xs text-gray-500 mb-2">Proceso</div>
            {/* Progress bar */}
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${checkPct}%` }}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
                <span className="text-xs text-gray-500">Completado</span>
                <span className="text-xs font-semibold text-gray-800">
                  {checkPct.toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
                <span className="text-xs text-gray-500">Pendiente</span>
                <span className="text-xs font-semibold text-gray-800">
                  {pendientePct.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 flex justify-end gap-2">
          {onDelete && (
            <button
              onClick={() => onDelete(auditoria.id)}
              className="border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              title="Mover a papelera"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          {onRiskMatrix && (
            <button
              onClick={() => onRiskMatrix(auditoria.id)}
              className="bg-orange-50 border border-orange-300 text-orange-700 hover:bg-orange-100 px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              Matriz de riesgo
            </button>
          )}
          <button
            onClick={() => navigate(`/auditorias/${auditoria.id}/informe-preliminar`)}
            className="bg-gray-50 border-2 border-gray-400 text-gray-700 hover:bg-gray-100 hover:border-gray-500 px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Crear Informe
          </button>
          {hasTasks && (
            <button
              onClick={() => setShowCronograma((p) => !p)}
              className="bg-sky-50 border border-sky-300 text-sky-700 hover:bg-sky-100 px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {showCronograma ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              Cronograma
            </button>
          )}
          <button
            onClick={() => onViewComplete(auditoria.id)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            Ver auditoría
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Cronograma expandible */}
        {showCronograma && hasTasks && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="flex items-center gap-2 mb-2 px-1">
              <svg className="w-4 h-4 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-semibold text-gray-700">Cronograma de trabajo</span>
              <span className="text-xs text-gray-400 ml-auto">
                {auditoria.tipo_auditoria ?? "Auditoría"} ·{" "}
                {auditoria.empresa?.razon_social ?? auditoria.razon_social}
              </span>
            </div>
            <CronogramaAuditoria auditoria={auditoria} />
          </div>
        )}
      </div>
    </div>
  );
};
